import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import { realLinkProps } from '../components/links/realLinkProps';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Checkbox,
  Radio,
  RadioGroup,
  Input,
  Field,
  Button,
} from '@fluentui/react-components';
import { OpenRegular, ClipboardTaskListLtrRegular, SaveRegular } from '@fluentui/react-icons';
import { useTasks, useUserPreferences, useSaveUserPreferences, OBLIGATION_TYPES } from '../services/complianceApi';
import { QuickViewPills } from '../components/listFilters/QuickViewPills';
import { TASK_PRESETS, DEFAULT_TASK_PRESET, activePreset, presetState } from '../utils/taskPresets';
import { useStateTaxClients, useSalesTaxAdmins } from '../services/stateTaxClientsApi';
import ColumnHeaderFilter from '../components/common/ColumnHeaderFilter';
import { LookupColumnFilter } from '../components/listFilters/LookupColumnFilter';
import {
  EMPTY_SELECTION, applyLookups, facetCountsFor, fromStored, isLookupActive, singleCode, singleSelection, toStored,
  type LookupColumn, type LookupSelection,
} from '../components/listFilters/lookupFilter';
import type { ComplianceTask } from '../types';
import { daysUntil, formatWholeDay, isTaskOverdue } from '../utils/complianceFormatters';
import { computeDueRange } from '../utils/dueRange';
import { includeTestClients, isTestClient } from '../utils/obligationHelpers';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';
import { pageShell } from '../components/layout/pageShell';
import TaskStatusBadge from '../components/compliance/TaskStatusBadge';

// Mirrors CosmosService.CrossPartitionTaskCap. When the cross-partition list query returns exactly
// this many rows, more tasks exist than were fetched — show a notice so the overflow isn't dropped
// silently. Server-side filters (status/date) keep the result under the cap. Client and assignee moved to the browser with the
// lookup combobox (C86, 2026-11-03), which needs every row the status and dates allow to count its options; the "All open" and
// "All completed" views always fetched that same set.
const TASK_LIST_CAP = 2000;

// Coarse status buckets, mirroring Dataverse statecode. Server expands these to stored statuses.
const BUCKETS = ['open', 'completed', 'cancelled'] as const;
type Bucket = (typeof BUCKETS)[number];

// Preset due-date ranges (radio list in the Due date header filter). 'custom' reveals
// explicit from/to pickers inside the same panel.
const DUE_RANGES = ['any', 'overdue', 'thisMonth', 'next30', 'thisQuarter', 'custom'] as const;
type DueRange = (typeof DUE_RANGES)[number];

type SortKey = 'dueDate' | 'clientName' | 'status' | 'assignedToName';

const useStyles = makeStyles({
  views: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  viewsNote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // A real link (fleet ruling 2026-10-29) that reads as the text it replaced: underlined on hover only.
  rowLinkText: {
    color: 'inherit',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: { fontSize: tokens.fontSizeHero800, fontWeight: tokens.fontWeightSemibold, lineHeight: '1.1', margin: 0 },
  subtitle: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  // STX-UX-01: the per-column filters moved into the column headers (Dataverse style);
  // the toolbar keeps only what is genuinely cross-column — free-text search — plus the
  // Clear-filters and Save-preferences actions.
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'end', flexWrap: 'wrap' },
  sortHeader: { display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit' },
  headerFlex: { display: 'inline-flex', alignItems: 'center', gap: '2px' },
  // Long option lists (clients, staff) scroll inside the header panel.
  panelScroll: {
    maxHeight: '260px', overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto', backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium,
  },
  // Keep every header's sort button + filter trigger inside its own cell — under ~1000px
  // the wrapper scrolls horizontally instead of letting header content overflow into (and
  // get covered by) the neighbouring cell.
  table: { minWidth: '1000px' },
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXL}`, textAlign: 'center',
    color: tokens.colorNeutralForeground3, backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium, border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  emptyIcon: { fontSize: '48px', color: tokens.colorNeutralForeground4 },
  emptyTitle: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
  emptyHint: { maxWidth: '480px', fontSize: tokens.fontSizeBase300, lineHeight: '1.5' },
  overdue: { color: tokens.colorPaletteRedForeground1 },
});

function filedCount(t: ComplianceTask): number {
  return t.filings.filter((f) => !!f.filedAt).length;
}

// Concise table labels for the verbose obligation display names.
const SHORT_OBLIGATION: Record<string, string> = {
  sales_tax: 'Sales Tax',
  solid_waste: 'Solid Waste',
  documentary_stamp_tax: 'Doc Stamp',
  communication_services_tax: 'CST',
};

// A task may span several obligations. Short labels for the cell; full names for the hover title.
function obligationShort(t: ComplianceTask, dash: string): string {
  const labels = Array.from(new Set(t.filings.map((f) => SHORT_OBLIGATION[f.obligationType] ?? f.obligationDisplayName)));
  return labels.length ? labels.join(', ') : dash;
}
function obligationFull(t: ComplianceTask): string {
  return Array.from(new Set(t.filings.map((f) => f.obligationDisplayName).filter(Boolean))).join(', ');
}

export default function ComplianceTasksPage() {
  const styles = useStyles();
  const linkTo = useRouterNavigate();
  const { t: tr } = useTranslation();
  const { accounts } = useMsal();

  // Filters: status and due date run server-side. Client, obligation and assignee are LOOKUP columns (fleet filter contract v3
  // §16.8 / C86, adopted 2026-11-03): each holds the shared `LookupSelection` (pick several, "Exclude these", "(Has no value)")
  // and filters in the browser, so each panel can count its options over every row the OTHER filters allow (§16.3).
  const [buckets, setBuckets] = useState<Bucket[]>(['open']);
  const [clientSel, setClientSel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [assigneeSel, setAssigneeSel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [dueRange, setDueRange] = useState<DueRange>('any');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [obligationSel, setObligationSel] = useState<LookupSelection>(EMPTY_SELECTION);
  const [search, setSearch] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('dueDate');
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [prefsSaved, setPrefsSaved] = useState<boolean>(false);

  const { dueAfter, dueBefore } = useMemo(
    () => computeDueRange(dueRange, customFrom, customTo),
    [dueRange, customFrom, customTo],
  );

  const filters = useMemo(
    () => ({
      buckets,
      dueAfter: dueAfter || undefined,
      dueBefore: dueBefore || undefined,
    }),
    [buckets, dueAfter, dueBefore],
  );
  const { data: tasks, isLoading, isError, error } = useTasks(filters);

  const { data: clients } = useStateTaxClients();
  const { data: staff } = useSalesTaxAdmins();

  // --- S4 batch2 --- Saved preferences + role-based defaults ─────────────────
  const { data: prefs, isSuccess: prefsLoaded } = useUserPreferences();
  const savePrefs = useSaveUserPreferences();

  // Current MSAL account — username is the UPN / email.
  const currentAccount = accounts[0] ?? null;
  const currentUserEmail = currentAccount?.username ?? '';

  // Resolve the signed-in user's staff row (same roster the Management page shows) by
  // email. Its `.id` is the Dataverse systemuser id — the SAME id space tasks store in
  // `assignedToId` (owner resolved via /systemusers). The AAD oid (localAccountId) is a
  // DIFFERENT id space and must NOT be used to filter tasks, or "my tasks" matches nothing.
  const currentStaff = useMemo(
    () =>
      (staff ?? []).find(
        (s) => s.email != null && s.email.toLowerCase() === currentUserEmail.toLowerCase(),
      ) ?? null,
    [staff, currentUserEmail],
  );

  // Apply saved prefs (or role-based defaults) once — only on first successful prefs load.
  // The ref guards against re-applying when other state changes cause a re-render.
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (!prefsLoaded || defaultApplied.current) return;
    defaultApplied.current = true;

    const saved = prefs?.taskFilters;
    if (saved) {
      // Restore every saved field that is present. A lookup column reads its C86 selection (`{op, value}`, the fleet's shape) when
      // saved, else the single id / type list every view saved before 2026-11-03.
      if (saved.buckets) setBuckets(saved.buckets as Bucket[]);
      if (saved.clientFilter) setClientSel(fromStored(saved.clientFilter));
      else if (saved.clientId) setClientSel(singleSelection(saved.clientId));
      if (saved.assigneeFilter) setAssigneeSel(fromStored(saved.assigneeFilter));
      else if (saved.assignedToId) setAssigneeSel(singleSelection(saved.assignedToId));
      if (saved.dueRange) setDueRange(saved.dueRange as DueRange);
      if (saved.customFrom !== undefined) setCustomFrom(saved.customFrom ?? '');
      if (saved.customTo !== undefined) setCustomTo(saved.customTo ?? '');
      if (saved.obligationFilter) setObligationSel(fromStored(saved.obligationFilter));
      else if (saved.obligationTypes?.length) setObligationSel({ ...EMPTY_SELECTION, codes: saved.obligationTypes });
    } else {
      // No saved prefs: the DEFAULT VIEW is "My open tasks" (DEFAULT_TASK_PRESET) for ANY user who resolves to a staff row. It is not
      // role-based. An earlier comment here claimed "admins see their own tasks; everyone else sees all", which the code never did
      // (BL-124). The view is now named in the UI by the preset pills and the "Opens on" line, so it is never an invisible filter.
      // Filter by the systemuser id (currentStaff.id), NOT the AAD oid.
      if (currentStaff) {
        setAssigneeSel(singleSelection(currentStaff.id));
      }
      // else: no staff row, so the "My" presets are disabled and the list shows all open tasks (the initial state above).
    }
  }, [prefsLoaded, prefs, currentStaff]);

  function handleSavePrefs() {
    // The C86 selections are saved in the fleet's `{op, value}` shape. The old fields are written too when a selection is plain
    // "include these" (one id for client and assignee), so a page still open on the previous build reads the same view.
    const clientOne = singleCode(clientSel);
    const assigneeOne = singleCode(assigneeSel);
    void savePrefs.mutateAsync({
      buckets,
      clientId: clientOne || undefined,
      assignedToId: assigneeOne || undefined,
      clientFilter: toStored(clientSel),
      assigneeFilter: toStored(assigneeSel),
      obligationFilter: toStored(obligationSel),
      dueRange,
      customFrom: customFrom || undefined,
      customTo: customTo || undefined,
      obligationTypes: isLookupActive(obligationSel) && !obligationSel.excluding && !obligationSel.includeNoValue
        ? obligationSel.codes : undefined,
    }).then(() => {
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    });
  }
  // ── end S4 batch2 ──────────────────────────────────────────────────────────

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  }
  // BL-124: which preset the status + assignee filters match right now (null = custom), and selecting one. A preset's assignee is
  // exactly one included person or nobody; any other assignee selection (several, excluding, "(Has no value)") is custom.
  const assigneeOne = singleCode(assigneeSel);
  const currentPreset = assigneeOne === null ? null : activePreset(buckets, assigneeOne, currentStaff?.id ?? null);
  function selectPreset(key: string) {
    const preset = TASK_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const next = presetState(preset, currentStaff?.id ?? null);
    setBuckets(next.buckets);
    setAssigneeSel(next.assignedToId ? singleSelection(next.assignedToId) : EMPTY_SELECTION);
  }

  function clearFilters() {
    setBuckets(['open']); setClientSel(EMPTY_SELECTION); setAssigneeSel(EMPTY_SELECTION);
    setDueRange('any'); setCustomFrom(''); setCustomTo('');
    setObligationSel(EMPTY_SELECTION); setSearch('');
  }

  // Obligation options for the header filter.
  //
  // 🔴 These are the app's obligation-type DOMAIN, unioned with whatever the current rows
  // happen to show — deliberately NOT "the types present in `tasks`", which is what this
  // used to be. `tasks` is the SERVER-FILTERED set, so deriving options from it meant the
  // filter's choices were narrowed by the very filters it exists to change. On the default
  // view (assignee = the signed-in user, per the role-based default below) an admin with no
  // open tasks of their own got zero rows and therefore an EMPTY filter panel, which only
  // "Clear filters" could revive. More generally: a filter sourced from filtered data can
  // only subtract — it can never offer a type that would broaden the view.
  //
  // The sibling roster page already did this correctly, sourcing every header filter from
  // the full client list; this page was the odd one out.
  //
  // Labels prefer Dataverse's own display name when a row supplies one, because that is the
  // wording staff see in the table cell; SHORT_OBLIGATION is the fallback for types the
  // current rows don't cover, and the raw value is the last resort for a type the frontend
  // mirror hasn't heard of yet.
  const obligationOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of tasks ?? [])
      for (const f of t.filings)
        if (!seen.has(f.obligationType) && f.obligationDisplayName)
          seen.set(f.obligationType, f.obligationDisplayName);

    const values = Array.from(new Set([...OBLIGATION_TYPES, ...seen.keys()]));
    return values
      .map((value) => ({ value, label: seen.get(value) ?? SHORT_OBLIGATION[value] ?? value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);

  // Client options: the whole roster (the combobox searches it), plus any client a fetched task names that the roster lacks. Sourced
  // from the roster, never from the filtered rows, for the reason the obligation options above give.
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clients ?? []) byId.set(c.stateTaxClientId, c.accountName);
    for (const t of tasks ?? []) if (!byId.has(t.clientId)) byId.set(t.clientId, t.clientName);
    return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [clients, tasks]);

  // Assignee options: the staff roster (by systemuser id, the id tasks store), plus anyone a fetched task is assigned to who isn't on it.
  const assigneeOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const s of staff ?? []) byId.set(s.id, s.name);
    for (const t of tasks ?? []) {
      if (t.assignedToId && !byId.has(t.assignedToId)) byId.set(t.assignedToId, t.assignedToName ?? t.assignedToId);
    }
    return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [staff, tasks]);

  const lookupColumns = useMemo<LookupColumn<ComplianceTask>[]>(() => [
    { key: 'client', selection: clientSel, values: (t) => [t.clientId] },
    { key: 'obligation', selection: obligationSel, values: (t) => t.filings.map((f) => f.obligationType) },
    { key: 'assignee', selection: assigneeSel, values: (t) => (t.assignedToId ? [t.assignedToId] : []) },
  ], [clientSel, obligationSel, assigneeSel]);

  // The rows every non-lookup filter allows: the facet base, and the list before its lookup filters.
  const baseRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Test clients are hidden from every staff list (Christian, 2026-10-20); the task-sync
    // pilot opts in with ?showTestClients=1 — see includeTestClients.
    // An explicit client filter is a deliberate choice of that client, so it shows even a test
    // client — the escape hatch this list had before the pilot. "Explicit" = included by name, not reached by excluding others.
    const showTest = includeTestClients() || (!clientSel.excluding && clientSel.codes.length > 0);
    return (tasks ?? [])
      .filter((t) => showTest || !isTestClient(t.clientName))
      .filter((t) => !q || t.clientName.toLowerCase().includes(q) || t.periodLabel.toLowerCase().includes(q));
  }, [tasks, search, clientSel]);

  // §16.3: each lookup column counts its options over the rows matching every OTHER filter.
  const facets = useMemo(() => ({
    client: facetCountsFor(baseRows, lookupColumns, 'client', clientOptions.map((o) => o.value)),
    obligation: facetCountsFor(baseRows, lookupColumns, 'obligation', obligationOptions.map((o) => o.value)),
    assignee: facetCountsFor(baseRows, lookupColumns, 'assignee', assigneeOptions.map((o) => o.value)),
  }), [baseRows, lookupColumns, clientOptions, obligationOptions, assigneeOptions]);

  // The lookup filters + sort over the base rows.
  const visible = useMemo(() => {
    const rows = applyLookups(baseRows, lookupColumns);
    const dir = sortAsc ? 1 : -1;
    const key = (t: ComplianceTask): string =>
      sortKey === 'clientName' ? t.clientName
        : sortKey === 'status' ? t.status
          : sortKey === 'assignedToName' ? (t.assignedToName ?? '')
            : t.dueDate;
    return rows.slice().sort((a, b) => key(a).localeCompare(key(b)) * dir);
  }, [baseRows, lookupColumns, sortKey, sortAsc]);

  // Plain render helpers, NOT components: defining these as components inside the page
  // would mint a new component type every render, remounting the header subtree and
  // resetting each ColumnHeaderFilter's open/close state on every filter change.
  const sortButton = (col: SortKey, label: string) => (
    <button type="button" className={styles.sortHeader} onClick={() => toggleSort(col)} aria-label={label}>
      {label}{sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : ''}
    </button>
  );

  // Header cell = optional sort button + optional Dataverse-style header filter.
  const headerCell = ({ col, label, filter }: { col?: SortKey; label?: string; filter?: ReactNode }) => (
    <TableHeaderCell>
      <span className={styles.headerFlex}>
        {col && label ? sortButton(col, label) : label}
        {filter}
      </span>
    </TableHeaderCell>
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text as="h1" className={styles.title}>{tr('tasksPage.title')}</Text>
        <Text className={styles.subtitle}>{tr('tasksPage.subtitle')}</Text>
      </div>

      {/* Cross-column tools only — per-column filters live in the column headers below. */}
      <div className={styles.filters}>
        <Field label={tr('tasksPage.filters.search')}>
          <Input value={search} onChange={(_e, d) => setSearch(d.value)} placeholder={tr('tasksPage.filters.searchPlaceholder')} />
        </Field>

        <Button appearance="subtle" onClick={clearFilters}>{tr('tasksPage.filters.clear')}</Button>

        {/* --- S4 batch2 --- Save current filter state as this user's default view */}
        <Button
          appearance="subtle"
          icon={<SaveRegular />}
          onClick={handleSavePrefs}
          disabled={savePrefs.isPending}
        >
          {prefsSaved ? tr('tasksPage.filters.preferencesSaved') : tr('tasksPage.filters.savePreferences')}
        </Button>
      </div>

      {/* BL-124: the active view is ALWAYS visible. A pill is filled only when the status + assignee filters match it exactly;
          otherwise the line says the filters are custom. Client, due date, obligation type and search layer on top of any preset. */}
      <div className={styles.views}>
        <QuickViewPills
          label={tr('tasksPage.views.label')}
          activeKey={currentPreset}
          onSelect={selectPreset}
          views={TASK_PRESETS.map((p) => ({
            key: p.key,
            label: tr(`tasksPage.views.${p.key}`),
            disabled: p.mine && !currentStaff,
            title: p.mine && !currentStaff ? tr('tasksPage.views.mineUnavailable') : undefined,
          }))}
        />
        <Text className={styles.viewsNote} data-testid="tasks-view-note">
          {currentPreset === null ? tr('tasksPage.views.custom') : tr('tasksPage.views.showing', { view: tr(`tasksPage.views.${currentPreset}`) })}
          {' · '}
          {prefs?.taskFilters ? tr('tasksPage.views.opensOnSaved') : tr('tasksPage.views.opensOnDefault', { view: tr(`tasksPage.views.${DEFAULT_TASK_PRESET}`) })}
        </Text>
      </div>

      {isLoading && <Spinner label={tr('tasksPage.loading')} />}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{apiErrorMessage(error, tr, 'tasksPage.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && (tasks?.length ?? 0) >= TASK_LIST_CAP && (
        <MessageBar intent="warning">
          <MessageBarBody>{tr('tasksPage.capNotice', { count: TASK_LIST_CAP })}</MessageBarBody>
        </MessageBar>
      )}

      {/* The table (and its header filters) stays mounted even when the current filters
          match zero rows — otherwise the only way OUT of an over-narrow filter would
          disappear with the rows. The empty state renders below the header instead. */}
      {!isLoading && !isError && (
        <div className={styles.tableWrapper}>
          <Table aria-label={tr('tasksPage.title')} className={styles.table}>
            <TableHeader>
              <TableRow>
                {headerCell({
                  col: 'clientName',
                  label: tr('tasksPage.columns.client'),
                  filter: (
                    <LookupColumnFilter
                      columnLabel={tr('tasksPage.columns.client')}
                      options={clientOptions}
                      value={clientSel}
                      onApply={setClientSel}
                      facetCounts={facets.client}
                    />
                  ),
                })}
                {headerCell({ label: tr('tasksPage.columns.period') })}
                {headerCell({
                  label: tr('tasksPage.columns.obligation'),
                  filter: (
                    <LookupColumnFilter
                      columnLabel={tr('tasksPage.columns.obligation')}
                      options={obligationOptions}
                      value={obligationSel}
                      onApply={setObligationSel}
                      facetCounts={facets.obligation}
                    />
                  ),
                })}
                {headerCell({
                  col: 'dueDate',
                  label: tr('tasksPage.columns.dueDate'),
                  filter: (
                    <ColumnHeaderFilter
                      columnLabel={tr('tasksPage.columns.dueDate')}
                      value={{ range: dueRange, from: customFrom, to: customTo }}
                      emptyValue={{ range: 'any' as DueRange, from: '', to: '' }}
                      isActive={(v) => v.range !== 'any'}
                      // The preset and its custom bounds are ONE filter: they commit together, in
                      // one render, so the query key changes once (C35).
                      onApply={(v) => { setDueRange(v.range); setCustomFrom(v.from); setCustomTo(v.to); }}
                    >
                      {(draft, setDraft) => (
                        <>
                          <RadioGroup value={draft.range} onChange={(_e, d) => setDraft({ ...draft, range: d.value as DueRange })}>
                            {DUE_RANGES.map((r) => (
                              <Radio key={r} value={r} label={tr(`tasksPage.dueRanges.${r}`)} />
                            ))}
                          </RadioGroup>
                          {draft.range === 'custom' && (
                            <>
                              <Field label={tr('tasksPage.filters.from')}>
                                <CalendarDateInput size="small" value={draft.from} onChange={(v) => setDraft({ ...draft, from: v })} />
                              </Field>
                              <Field label={tr('tasksPage.filters.to')}>
                                <CalendarDateInput size="small" value={draft.to} onChange={(v) => setDraft({ ...draft, to: v })} />
                              </Field>
                            </>
                          )}
                        </>
                      )}
                    </ColumnHeaderFilter>
                  ),
                })}
                {headerCell({ label: tr('tasksPage.columns.filings') })}
                {headerCell({
                  col: 'status',
                  label: tr('tasksPage.columns.status'),
                  filter: (
                    <ColumnHeaderFilter
                      columnLabel={tr('tasksPage.columns.status')}
                      value={buckets}
                      emptyValue={[] as Bucket[]}
                      isActive={(v) => v.length > 0}
                      onApply={setBuckets}
                      // The status filter is active BY DEFAULT (Open bucket) — a filled
                      // funnel icon alone doesn't read as "this list is filtered", so the
                      // selected bucket(s) render as a chip right in the header.
                      activeValueLabel={buckets.map((b) => tr(`tasksPage.buckets.${b}`)).join(', ')}
                    >
                      {(draft, setDraft) => (<>
                      {BUCKETS.map((b) => (
                        <Checkbox
                          key={b}
                          label={tr(`tasksPage.buckets.${b}`)}
                          checked={draft.includes(b)}
                          onChange={(_e, d) =>
                            setDraft(d.checked ? [...draft, b] : draft.filter((x) => x !== b))}
                        />
                      ))}
                      </>)}
                    </ColumnHeaderFilter>
                  ),
                })}
                {headerCell({
                  col: 'assignedToName',
                  label: tr('tasksPage.columns.assignee'),
                  filter: (
                    <LookupColumnFilter
                      columnLabel={tr('tasksPage.columns.assignee')}
                      options={assigneeOptions}
                      value={assigneeSel}
                      onApply={setAssigneeSel}
                      facetCounts={facets.assignee}
                      align="end"
                    />
                  ),
                })}
                <TableHeaderCell></TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((t) => {
                const days = daysUntil(t.dueDate);
                const isOverdue = isTaskOverdue(t);
                const taskHref = `/compliance/tasks/${t.id}?clientId=${encodeURIComponent(t.clientId)}`;
                return (
                  <TableRow key={t.id} className={ROW_LINK_HOST} style={{ cursor: 'pointer' }}>
                    <TableCell>
                      <TableCellLayout>
                        <RowLink href={taskHref} rowClick="double" className={styles.rowLinkText}>{t.clientName}</RowLink>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell><TableCellLayout>{t.periodLabel}</TableCellLayout></TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <span title={obligationFull(t)}>{obligationShort(t, tr('common.dash'))}</span>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout
                        description={
                          isOverdue ? tr('tasksPage.daysOverdue', { count: Math.abs(days) })
                            : days >= 0 ? tr('tasksPage.inDays', { count: days }) : undefined
                        }
                      >
                        <span className={isOverdue ? styles.overdue : undefined}>{formatWholeDay(t.dueDate)}</span>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell><TableCellLayout>{filedCount(t)} / {t.filings.length}</TableCellLayout></TableCell>
                    <TableCell>
                      <TableCellLayout>
                        <TaskStatusBadge task={t} />
                      </TableCellLayout>
                    </TableCell>
                    <TableCell><TableCellLayout>{t.assignedToName ?? tr('common.dash')}</TableCellLayout></TableCell>
                    <TableCell>
                      <Button
                        as="a"
                        appearance="subtle" icon={<OpenRegular />} size="small"
                        {...realLinkProps(taskHref, { navigate: linkTo })}
                      >
                        {tr('tasksPage.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!isLoading && !isError && visible.length === 0 && (
        <div className={styles.emptyState}>
          <ClipboardTaskListLtrRegular className={styles.emptyIcon} />
          <Text className={styles.emptyTitle}>{tr('tasksPage.empty.title')}</Text>
          <Text className={styles.emptyHint}>{tr('tasksPage.empty.hint')}</Text>
        </div>
      )}
    </div>
  );
}
